import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddOfficerGroupModal from './AddOfficerGroupModal';
import { hasPermission } from '../../../utils/permissions';
import {
  fetchOfficerGroups,
  fetchOfficerGroupById,
  addOfficerGroup,
  updateOfficerGroup,
  selectOfficerGroups,
  selectOfficerGroupsLoading,
  selectOfficerGroupsError,
  selectOfficerGroupsSuccessMessage,
  selectOfficerGroupsTotal,
  selectOfficerGroupsPage,
  selectOfficerGroupsPageSize,
  selectOfficerGroupsSortColumn,
  selectOfficerGroupsSortDirection,
  selectOfficerGroupsFilterName,
  selectSelectedOfficerGroup,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  deleteOfficerGroup,
  bulkDeleteOfficerGroups,
  clearError,
  clearSuccessMessage,
} from './slice';

function OfficerGroups({ onSelectedIdsChange, onAddClick, onBulkDeleteClick, hideButtons = false, isActive = true }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const officerGroups = useSelector(selectOfficerGroups);
  const loading = useSelector(selectOfficerGroupsLoading);
  const error = useSelector(selectOfficerGroupsError);
  const successMessage = useSelector(selectOfficerGroupsSuccessMessage);
  const total = useSelector(selectOfficerGroupsTotal);
  const page = useSelector(selectOfficerGroupsPage);
  const pageSize = useSelector(selectOfficerGroupsPageSize);
  const sortColumn = useSelector(selectOfficerGroupsSortColumn);
  const sortDirection = useSelector(selectOfficerGroupsSortDirection);
  const filterName = useSelector(selectOfficerGroupsFilterName);
  const selectedOfficerGroup = useSelector(selectSelectedOfficerGroup);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [officerGroupToDelete, setOfficerGroupToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Check permissions
  const canAddOfficerGroup = hasPermission('officergroup', 'addOfficerGroup');
  const canEditOfficerGroup = hasPermission('officergroup', 'editOfficerGroup');
  const canDeleteOfficerGroup = hasPermission('officergroup', 'deleteOfficerGroup');

  // Handler for selection change that also notifies parent
  const handleSelectionChange = useCallback((ids) => {
    setSelectedIds(ids);
    // Only notify parent if callback is provided and we're in embedded mode
    if (onSelectedIdsChange && hideButtons) {
      onSelectedIdsChange(ids);
    }
  }, [onSelectedIdsChange, hideButtons]);

  // Use local selectedIds
  const effectiveSelectedIds = selectedIds;

  // Fetch data whenever parent tab changes or when filters change
  useEffect(() => {
    dispatch(fetchOfficerGroups({ page, pageSize, sortColumn, sortDirection, name: filterName }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, isActive]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      dispatch(fetchOfficerGroups({ page, pageSize, sortColumn, sortDirection, name: filterName }));
    }
  }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

  // Define table columns
  const columns = [
    {
      key: 'name',
      label: t('officerGroups.table.name'),
    },
    {
      key: 'description',
      label: t('officerGroups.table.description'),
    },
    {
      key: 'updatedOn',
      label: t('officerGroups.table.updatedOn'),
      render: (value) => (value ? new Date(value).toLocaleDateString('vi-VN') : '-'),
    },
  ];

  // Handle edit action
  const handleEdit = (row) => {
    dispatch(fetchOfficerGroupById(row.id));
    setShowEditDialog(true);
  };

  // Handle delete action
  const handleDelete = (row) => {
    setOfficerGroupToDelete(row);
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (officerGroupToDelete?.id) {
      dispatch(deleteOfficerGroup(officerGroupToDelete.id));
    }
    setShowDeleteDialog(false);
    setOfficerGroupToDelete(null);
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setOfficerGroupToDelete(null);
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (onBulkDeleteClick) {
      onBulkDeleteClick();
    } else {
      setShowBulkDeleteDialog(true);
    }
  };

  // Confirm bulk delete action
  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteOfficerGroups(effectiveSelectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  // Cancel bulk delete action
  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
  };

  // Handle sort change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    dispatch(setFilterName(event.target.value));
  };

  // Handle add officer group
  const handleAddOfficerGroup = () => {
    if (onAddClick) {
      onAddClick();
    } else {
      setShowAddDialog(true);
    }
  };

  // Handle close add officer group dialog
  const handleCloseAddOfficerGroup = () => {
    setShowAddDialog(false);
  };

  // Confirm add officer group
  const handleConfirmAddOfficerGroup = (officerGroupData) => {
    dispatch(addOfficerGroup(officerGroupData));
    setShowAddDialog(false);
  };

  // Handle close edit officer group dialog
  const handleCloseEditOfficerGroup = () => {
    setShowEditDialog(false);
  };

  // Confirm edit officer group
  const handleConfirmEditOfficerGroup = (officerGroupData) => {
    dispatch(updateOfficerGroup(officerGroupData));
    setShowEditDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, overflow: 'hidden' }}>
      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', minHeight: 0 }}>
        {/* Action Buttons - Only show if not hidden */}
        {!hideButtons && (
          <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {canAddOfficerGroup && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddOfficerGroup}
                id="addOfficerGroup"
                name="addOfficerGroup"
                data-testid="officerGroups-button-addOfficerGroup"
              >
                {t('officerGroups.addOfficerGroup')}
              </Button>
            )}
            {canDeleteOfficerGroup && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={effectiveSelectedIds.length === 0}
                id="deleteSelected"
                name="deleteSelected"
                data-testid="officerGroups-button-deleteSelected"
              >
                {t('officerGroups.deleteSelected')}
              </Button>
            )}
          </Box>
        )}
        {/* Filter Section */}
        <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Input
                label={t('officerGroups.filterByName')}
                value={filterName}
                onChange={handleFilterChange}
                placeholder={t('officerGroups.filterByNamePlaceholder')}
                id="filterName"
                name="filterName"
                data-testid="officerGroups-input-filterName"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff', minHeight: 0 }}>
          <Table
            id="officerGroups-table"
            columns={columns}
            data={officerGroups}
            selectable={true}
            selectedIds={effectiveSelectedIds}
            onSelectionChange={handleSelectionChange}
            onEdit={canEditOfficerGroup ? handleEdit : null}
            onDelete={canDeleteOfficerGroup ? handleDelete : null}
            loading={loading}
            emptyMessage={t('officerGroups.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            sortableColumns={['name', 'description', 'updatedOn']}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('officerGroups.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('officerGroups.deleteDialog.message', {
              name: officerGroupToDelete?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="officerGroups-button-cancelDelete"
          >
            {t('officerGroups.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="officerGroups-button-confirmDelete"
          >
            {t('officerGroups.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('officerGroups.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('officerGroups.bulkDeleteDialog.message', {
              count: effectiveSelectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="officerGroups-button-cancelBulkDelete"
          >
            {t('officerGroups.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="officerGroups-button-confirmBulkDelete"
          >
            {t('officerGroups.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Officer Group Modal */}
      <AddOfficerGroupModal
        open={showAddDialog}
        onClose={handleCloseAddOfficerGroup}
        onConfirm={handleConfirmAddOfficerGroup}
      />

      {/* Edit Officer Group Modal */}
      <AddOfficerGroupModal
        open={showEditDialog}
        onClose={handleCloseEditOfficerGroup}
        onConfirm={handleConfirmEditOfficerGroup}
        officerGroupData={selectedOfficerGroup}
        editMode={true}
      />
    </Box>
  );
}

export default OfficerGroups;

